import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { CMSProvider } from "./context/CMSContext";
import { LanguageProvider } from "./context/LanguageContext";

// One Green Dev Pages
import LandingPage from "./pages/LandingPage";
import SongonEastSidePage from "./pages/SongonEastSidePage";
import AboutPage from "./pages/AboutPage";
import ADNPage from "./pages/ADNPage";

// Admin Pages
import { AdminAuthProvider, useAdminAuth } from "./admin/context/AdminAuthContext";
import AdminLoginPage from "./admin/pages/AdminLoginPage";
import AdminDashboardPage from "./admin/pages/AdminDashboardPage";
import AdminContentPage from "./admin/pages/AdminContentPage";
import AdminAudiencePage from "./admin/pages/AdminAudiencePage";
import AdminSettingsPage from "./admin/pages/AdminSettingsPage";
import AdminProspectsPage from "./admin/pages/AdminProspectsPage";
import AdminForgotPasswordPage from "./admin/pages/AdminForgotPasswordPage";
import AdminResetPasswordPage from "./admin/pages/AdminResetPasswordPage";

// Protected Route for Admin
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAdminAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#faf9f6' }}>
        <div className="w-5 h-5 border-2 rounded-full animate-spin" style={{ borderColor: '#e8e5e0', borderTopColor: '#1e4a1a' }}></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/ogd-access-gate" state={{ from: location }} replace />;
  }

  return children;
};

// Admin Router
const AdminRouter = () => {
  const { isAuthenticated, loading } = useAdminAuth();
  const location = useLocation();

  // Redirect to dashboard if already logged in and on login page
  if (!loading && isAuthenticated && (location.pathname === '/admin/login' || location.pathname === '/ogd-access-gate')) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <Routes>
      <Route path="/ogd-access-gate" element={<AdminLoginPage />} />
      <Route path="/ogd-access-gate/forgot-password" element={<AdminForgotPasswordPage />} />
      <Route path="/ogd-access-gate/reset-password" element={<AdminResetPasswordPage />} />
      <Route path="/admin/login" element={<Navigate to="/ogd-access-gate" replace />} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboardPage /></ProtectedRoute>} />
      <Route path="/admin/content" element={<ProtectedRoute><AdminContentPage /></ProtectedRoute>} />
      <Route path="/admin/prospects" element={<ProtectedRoute><AdminProspectsPage /></ProtectedRoute>} />
      <Route path="/admin/audience" element={<ProtectedRoute><AdminAudiencePage /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute><AdminSettingsPage /></ProtectedRoute>} />
      <Route path="/admin/*" element={<Navigate to="/admin" replace />} />
    </Routes>
  );
};

// Public Site Router
const SiteRouter = () => {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/songon-east-side" element={<SongonEastSidePage />} />
      <Route path="/a-propos" element={<Navigate to="/adn-ogd" replace />} />
      <Route path="/adn-ogd" element={<ADNPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  const location = window.location;
  
  // Admin routes are completely separate
  if (location.pathname.startsWith('/admin') || location.pathname.startsWith('/ogd-access-gate')) {
    return (
      <div className="App">
        <BrowserRouter>
          <AdminAuthProvider>
            <AdminRouter />
          </AdminAuthProvider>
        </BrowserRouter>
      </div>
    );
  }

  // Public site with CMS and Language context
  return (
    <div className="App">
      <LanguageProvider>
        <CMSProvider>
          <BrowserRouter>
            <SiteRouter />
          </BrowserRouter>
        </CMSProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
